import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Send, Volume2, VolumeX, Loader2, Trash2, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export default function HealthCoach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [recording, setRecording] = useState(false);
  const [sessionId] = useState(() => `coach_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load chat history
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;

    fetch(`${API_URL}/api/v2/health-coach/history?session_id=${sessionId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.chats && data.chats.length > 0) {
          setMessages(data.chats.reverse().map(c => ({ role: c.role, content: c.content })));
        } else {
          // Welcome message
          setMessages([{
            role: 'assistant',
            content: "Hi! I'm your AI Health Coach. I have access to your blood sugar readings, nutrition logs, medications, and activity data. Ask me anything — like \"How did my blood sugar look this week?\" or \"What should I eat for dinner?\""
          }]);
        }
      })
      .catch(() => {
        setMessages([{
          role: 'assistant',
          content: "Hi! I'm your AI Health Coach. Ask me anything about your health data, meals, or glucose management."
        }]);
      });
  }, [sessionId]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('gym_notebook_auth_token');
      const res = await fetch(`${API_URL}/api/v2/health-coach/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        signal: AbortSignal.timeout(120000),
        body: JSON.stringify({
          message: text.trim(),
          voice_response: voiceEnabled,
          session_id: sessionId,
        }),
      });

      const data = await res.json();
      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);

        // Play voice if available
        if (data.audio_base64 && voiceEnabled) {
          playAudio(data.audio_base64);
        }
      } else {
        toast.error(data.detail || 'Failed to get response');
      }
    } catch (e) {
      toast.error(e.message || 'Connection failed');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble responding. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const playAudio = (base64) => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audioRef.current = audio;
      audio.play().catch(() => {});
    } catch (e) {
      // Silent fail for audio
    }
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());

        // Send to Whisper for transcription
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL || '';
          const token = localStorage.getItem('gym_notebook_auth_token');
          const formData = new FormData();
          formData.append('audio', blob, 'voice.webm');

          const res = await fetch(`${API_URL}/api/v2/nutrition/voice-food`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
          });
          const data = await res.json();

          if (data.task_id) {
            // Poll for transcription
            const pollResult = async () => {
              for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 1000));
                const statusRes = await fetch(`${API_URL}/api/v2/nutrition/voice-food-status/${data.task_id}`, {
                  headers: { 'Authorization': `Bearer ${token}` },
                });
                const status = await statusRes.json();
                if (status.status === 'done' && status.transcript) {
                  sendMessage(status.transcript);
                  return;
                }
                if (status.status === 'error') {
                  toast.error('Voice transcription failed');
                  return;
                }
              }
              toast.error('Voice transcription timed out');
            };
            pollResult();
          }
        } catch {
          toast.error('Failed to process voice');
        }
      };

      mediaRecorder.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const clearHistory = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('gym_notebook_auth_token');
    await fetch(`${API_URL}/api/v2/health-coach/history`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    setMessages([{
      role: 'assistant',
      content: "Chat cleared. How can I help you today?"
    }]);
  };

  return (
    <div className="flex flex-col h-full bg-slate-900" data-testid="health-coach">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur-xl">
        <div className="flex items-center gap-2 flex-1">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-pink-500/20">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <h1 className="text-lg font-heading font-bold tracking-tight text-white">Health Coach</h1>
        </div>
        <button
          onClick={() => setVoiceEnabled(!voiceEnabled)}
          className={`p-2 rounded-lg ${voiceEnabled ? 'text-purple-400 bg-purple-500/10' : 'text-slate-500'}`}
          data-testid="toggle-voice"
        >
          {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
        <button
          onClick={clearHistory}
          className="p-2 rounded-lg text-slate-500 hover:text-red-400"
          data-testid="clear-chat"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
              msg.role === 'user'
                ? 'bg-purple-600 text-white rounded-br-md'
                : 'bg-slate-800 text-slate-200 rounded-bl-md border border-slate-700'
            }`}>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-800 rounded-2xl rounded-bl-md border border-slate-700 px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
                <span className="text-xs text-slate-400">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-800 p-3 bg-slate-900/95 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={recording ? stopRecording : startRecording}
            className={`p-2.5 rounded-xl transition-colors ${
              recording
                ? 'bg-red-500 text-white animate-pulse'
                : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            data-testid="voice-record-btn"
          >
            {recording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
            placeholder={recording ? 'Listening...' : 'Ask about your health...'}
            className="flex-1 bg-slate-800 text-white rounded-xl px-4 py-2.5 text-sm border border-slate-700 outline-none focus:border-purple-500 placeholder-slate-500"
            disabled={loading || recording}
            data-testid="coach-input"
          />
          <Button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className="bg-purple-600 hover:bg-purple-500 rounded-xl px-3"
            data-testid="coach-send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        {recording && (
          <p className="text-center text-[10px] text-red-400 mt-1 animate-pulse">
            Recording... tap to stop
          </p>
        )}
      </div>
    </div>
  );
}
